import _ from "lodash";

import { Order } from "@models/Order";
import { OrderSet } from "@models/OrderSet";
import { TriggeredRuleStatus } from "@models/TriggeredRule";

const COMPLIANCE_WARNING_TYPES = [
  "price",
  "prior-approval",
  "coupon-prior-approval",
  "internal-approval",
  "coupon-internal-approval",
] as const;

type ComplianceWarningType = (typeof COMPLIANCE_WARNING_TYPES)[number];

export type ComplianceWarning = {
  sku: string;
  state: string | null;
  status: TriggeredRuleStatus;
  type: ComplianceWarningType | "other";
};

export function getComplianceWarnings(orders: Order[]): ComplianceWarning[] {
  const warnings: ComplianceWarning[] = [];

  for (const order of orders) {
    for (const orderVariant of order.orderVariants) {
      if (orderVariant.qty === 0 || orderVariant.overridesCompliance) continue;

      const rules = [
        ...orderVariant.triggeredRules,
        ...orderVariant.priorApprovalTriggeredRules,
      ];
      for (const rule of rules) {
        if (rule.status !== "approved") {
          warnings.push({
            sku: orderVariant.variant.variantSku,
            state: order.address?.state?.code ?? null,
            status: rule.status,
            type: COMPLIANCE_WARNING_TYPES.includes(
              rule.ruleType as ComplianceWarningType
            )
              ? (rule.ruleType as ComplianceWarningType)
              : "other",
          });
        }
      }
    }
  }

  return _.uniqBy(warnings, (w) => `${w.sku}-${w.state}-${w.status}-${w.type}`);
}

/**
 * Inventory order sets must not be approved until every line is compliance-`ok`
 * (no pending holds, no denied / in-violation rules, incl. price).
 */
export function canApproveOrderSet(orderSet: OrderSet): boolean {
  const { type: orderSetType, status: orderSetStatus } = orderSet;
  if (orderSetStatus === "in-progress") return false;
  if (orderSetType !== "from-inventory") return true;
  if (orderSetStatus !== "submitted") return false;
  if (orderSet.hasUnresolvedComplianceHolds) return false;
  return true;
}
